// app/src/components/common/MessageDialog.tsx

import React from "react"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from "@mui/material"
import { CheckCircle, Error, Warning, Info } from "@mui/icons-material"

export type MessageType = "success" | "error" | "warning" | "info"

interface MessageDialogProps {
  open: boolean
  title?: string
  message: string
  type?: MessageType
  messageAlign?: "left" | "center"
  container?: Element | null
  onClose: () => void
  onConfirm?: () => void
  confirmText?: string
  cancelText?: string
  showCancel?: boolean
}

const MessageDialog: React.FC<MessageDialogProps> = ({
  open,
  title,
  message,
  type = "info",
  messageAlign = "left",
  container,
  onClose,
  onConfirm,
  confirmText = "확인",
  cancelText = "취소",
  showCancel = false,
}) => {
  const messageLines = message.split("\n")
  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle sx={{ color: "#4caf50", fontSize: 24 }} />
      case "error":
        return <Error sx={{ color: "#f44336", fontSize: 24 }} />
      case "warning":
        return <Warning sx={{ color: "#ff9800", fontSize: 24 }} />
      case "info":
      default:
        return <Info sx={{ color: "#2196f3", fontSize: 24 }} />
    }
  }

  const getTitle = () => {
    if (title) return title

    switch (type) {
      case "success":
        return "성공"
      case "error":
        return "오류"
      case "warning":
        return "경고"
      case "info":
      default:
        return "알림"
    }
  }

  const getColor = () => {
    switch (type) {
      case "success":
        return "#4caf50"
      case "error":
        return "#f44336"
      case "warning":
        return "#ff9800"
      case "info":
      default:
        return "#2196f3"
    }
  }

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm()
    } else {
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      container={container}
      maxWidth="sm"
      fullWidth
      aria-labelledby="message-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 400,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `2px solid ${getColor()}`,
          pb: 2,
        }}
      >
        {getIcon()}
        <Typography id="message-dialog-title" component="h2" variant="h6" sx={{ fontWeight: "bold", color: getColor() }}>
          {getTitle()}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ textAlign: messageAlign }}>
          {messageLines.map((line, index) => (
            <Typography
              key={`${line}-${index}`}
              variant="body1"
              sx={{
                lineHeight: 1.6,
                wordBreak: "break-word",
                mt: index > 0 ? 1.0 : 2,
              }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        {showCancel && (
          <Button onClick={onClose} variant="outlined" sx={{ minWidth: 80 }}>
            {cancelText}
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          autoFocus
          sx={{
            minWidth: 80,
            backgroundColor: getColor(),
            "&:hover": {
              backgroundColor: getColor(),
              filter: "brightness(0.9)",
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MessageDialog

